import React, { useMemo, useState, useCallback } from "react";
import { useVirtualScroll } from "../hooks/useVirtualScroll";
import {
  FaEdit,
  FaArrowLeft,
  FaArrowRight,
  FaTrash,
  FaEye,
  FaCog,
  FaLayerGroup,
  FaList,
} from "react-icons/fa";
import {
  getSeenStatus,
  shouldClearSeenOnEyeClick,
} from "../utils/validation";
import { formatAssignedPersonnelList } from "../utils/personnelDisplay";
import {
  childHasFirstAllocation,
  getUnitIdForAssigneeKey,
} from "../pages/Inventory/utils/inventoryUnitFilter";
import { ClickableInventoryItemName } from "./ClickableInventoryItemName";

// Optimized column configuration
const COLUMN_CONFIG = {
  itemName: { sortable: true },
  serialNumber: { sortable: true },
  isOrganic: { sortable: true },
  quantity: { sortable: true },
  assignedQuantity: { sortable: false },
  availableQuantity: { sortable: false },
  status: { sortable: true },
  assignedTo: { sortable: true },
  assignedBy: { sortable: false },
  currentUnitId: { sortable: true, hasBorder: true },
  locationNote: { sortable: false },
  seenAt: { sortable: true },
  actions: { sortable: false, centered: true },
};

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  itemName: 150,
  serialNumber: 120,
  isOrganic: 70,
  quantity: 100,
  assignedQuantity: 100,
  availableQuantity: 100,
  status: 100,
  assignedTo: 120,
  assignedBy: 120,
  currentUnitId: 100,
  locationNote: 140,
  seenAt: 80,
  actions: 120,
};

/** Extra px per column when serial rows are grouped (wider cells for summary text). */
const GROUPED_MODE_COLUMN_WIDTH_EXTRA = 28;
const GROUPED_MODE_MAX_COLUMN_WIDTH = 300;

// Helper function to get column style with dynamic widths
const getColumnStyle = (key, columnWidths, additionalStyles = {}) => {
  const config = COLUMN_CONFIG[key];
  if (!config) return {};

  return {
    width: `${columnWidths[key]}px`,
    minWidth: "50px",
    maxWidth: "300px",
    padding: "8px",
    boxSizing: "border-box",
    ...(config.hasBorder && { borderRight: "1px solid #e0e0e0" }),
    ...(config.centered && {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
    }),
    ...additionalStyles,
  };
};

const getItemTypeClass = (isOrganic) =>
  isOrganic ? "item-type organic" : "item-type synthetic";

const getItemTypeText = (isOrganic) => (isOrganic ? "אורגני" : "קווי");

const getVirtualRowClassName = (status) => {
  switch (status) {
    case "assigned":
      return "virtual-row assigned";
    case "returned":
      return "virtual-row returned";
    default:
      return "virtual-row";
  }
};

const getVirtualStatusText = (status) => {
  switch (status) {
    case "assigned":
      return "מוחתם";
    case "returned":
      return "זוכה";
    default:
      return "זמין";
  }
};

/** Module scope: same reference every parent render — avoids remounting rows and scroll jump */
const VirtualInventoryRow = React.memo(function VirtualInventoryRow({
  item,
  height,
  columnWidths,
  hideGroupedColumns = false,
  getUnitName,
  personnelUnitMap,
  handleEdit,
  handleReturnClick,
  handleMakeAvailable,
  handleDelete,
  handleConfirmSeen,
  userPermissions,
  userRole,
  childUserUnitId,
  childUserPersonalNumber = null,
  resolveAssignedByName,
  onItemNameClick,
  onAssignedNameClick,
  seenSettings,
  editingNoteId,
  setEditingNoteId,
  noteValue,
  setNoteValue,
  editingNoteMeta,
  setEditingNoteMeta,
  onUpdateNote,
  nicknameByPersonalNumber = new Map(),
  personnel = null,
}) {
  const isGroup = item.isSerialGroupRow === true;
  const isSerial =
    !isGroup && item.serialNumber && item.serialNumber.trim() !== "";
  return (
    <div
      className={`${getVirtualRowClassName(
        item.calculatedStatus || item.status,
      )}${isGroup ? " virtual-row-serial-group" : ""}`}
      style={{ height, display: "flex", alignItems: "center" }}
    >
      <div
        className="virtual-cell"
        style={getColumnStyle("itemName", columnWidths)}
      >
        <ClickableInventoryItemName
          item={item}
          onItemNameClick={onItemNameClick}
        />
      </div>
      <div
        className="virtual-cell virtual-cell-serial-group-wrap"
        style={getColumnStyle("serialNumber", columnWidths)}
        title={
          isGroup && item.serialGroupStatusSummary
            ? `${item.serialGroupSerialCount} סידוריים — ${item.serialGroupStatusSummary}`
            : undefined
        }
      >
        {isGroup && item.serialGroupSerialCount != null ? (
          <div className="serial-group-serial-cell serial-group-serial-cell-virtual-one-line">
            {item.serialGroupStatusSummary
              ? `${item.serialGroupSerialCount} סידוריים — ${item.serialGroupStatusSummary}`
              : `${item.serialGroupSerialCount} סידוריים`}
          </div>
        ) : (
          item.serialNumber || "-"
        )}
      </div>
      <div
        className="virtual-cell"
        style={getColumnStyle("isOrganic", columnWidths)}
      >
        <span className={getItemTypeClass(item.isOrganic)}>
          {getItemTypeText(item.isOrganic)}
        </span>
      </div>
      <div
        className="virtual-cell"
        style={getColumnStyle("quantity", columnWidths)}
      >
        {item.displayQuantity ?? item.quantity}
      </div>
      <div
        className="virtual-cell"
        style={getColumnStyle("assignedQuantity", columnWidths)}
      >
        {item.assignedQuantity ?? 0}
      </div>
      <div
        className="virtual-cell"
        style={getColumnStyle("availableQuantity", columnWidths)}
      >
        {item.availableQuantity ?? 0}
      </div>
      {!hideGroupedColumns && (
        <>
      <div
        className="virtual-cell"
        style={getColumnStyle("status", columnWidths)}
      >
        <span className="status-badge">
          {getVirtualStatusText(item.calculatedStatus ?? item.status)}
        </span>
      </div>
      <div
        className="virtual-cell"
        style={getColumnStyle("assignedTo", columnWidths)}
      >
        {(() => {
          if (!isSerial) return "";

          const assignedText =
            item.assignedPersonnel && item.assignedPersonnel.length > 0
              ? formatAssignedPersonnelList(
                  item.assignedPersonnel,
                  nicknameByPersonalNumber,
                  personnel,
                )
              : "-";

          if (
            isSerial &&
            (item.calculatedStatus === "assigned" ||
              item.status === "assigned") &&
            item.assignedPersonnel?.length > 0
          ) {
            return (
              <span
                className="clickable-assigned-name"
                onClick={() => onAssignedNameClick?.(item)}
              >
                {assignedText}
              </span>
            );
          }
          return assignedText;
        })()}
      </div>
      {userRole !== "childUser" && (
        <div
          className="virtual-cell"
          style={getColumnStyle("assignedBy", columnWidths)}
        >
          {item.assignedPersonnel &&
          item.assignedPersonnel.length > 0 &&
          (item.calculatedStatus === "assigned" || item.status === "assigned")
            ? resolveAssignedByName?.(
                item.assignedPersonnel[0].assignedBy
              ) || "-"
            : "-"}
        </div>
      )}
      <div
        className="virtual-cell"
        style={getColumnStyle("currentUnitId", columnWidths)}
      >
        {(() => {
          if (userRole === "childUser" && childUserUnitId) {
            if (isSerial) {
              return getUnitName(childUserUnitId);
            }
          }

          if (
            item.assignedPersonnel &&
            item.assignedPersonnel.length > 0 &&
            (item.calculatedStatus === "assigned" ||
              item.status === "assigned")
          ) {
            const firstEmployeePersonalNumber =
              item.assignedPersonnel[0].personalNumber;
            const employeeUnitId = getUnitIdForAssigneeKey(
              personnelUnitMap,
              firstEmployeePersonalNumber,
            );
            return employeeUnitId ? getUnitName(employeeUnitId) : "";
          }
          return getUnitName(item.currentUnitId);
        })()}
      </div>
        </>
      )}
      {!hideGroupedColumns && (
        <div
          className="virtual-cell note-column-cell"
          style={getColumnStyle("locationNote", columnWidths)}
        >
          {isGroup ? (
            <span className="note-cell-empty">—</span>
          ) : (
            (() => {
          let note = item.assignedPersonnel?.[0]?.note || "";
          let isChildNote = false;

          if (userRole !== "childUser" && !note) {
            if (isSerial) {
              const secondAssignments = item.assignments?.second || {};
              const firstChildEntry = Object.values(secondAssignments)[0];
              if (firstChildEntry?.note) {
                note = firstChildEntry.note;
                isChildNote = true;
              }
            }
          }

          const isUnitPeerNoteReadonly =
            userRole === "childUser" &&
            childUserPersonalNumber &&
            !childHasFirstAllocation(item, childUserPersonalNumber);
          const noteReadonly = isChildNote || isUnitPeerNoteReadonly;

          const isEditing = editingNoteId === item.id && !noteReadonly;
          if (isEditing) {
            return (
              <textarea
                className="note-cell-textarea"
                autoFocus
                value={noteValue}
                onFocus={(e) => {
                  const len = e.target.value.length;
                  e.target.setSelectionRange(len, len);
                }}
                onChange={(e) => setNoteValue(e.target.value)}
                onBlur={() => {
                  if (
                    onUpdateNote &&
                    editingNoteMeta?.personalNumber &&
                    noteValue !== editingNoteMeta.originalNote
                  ) {
                    onUpdateNote(
                      editingNoteMeta.itemId,
                      editingNoteMeta.personalNumber,
                      editingNoteMeta.level,
                      noteValue
                    );
                  }
                  setEditingNoteId(null);
                  setEditingNoteMeta(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setEditingNoteId(null);
                    setEditingNoteMeta(null);
                  }
                }}
              />
            );
          }
          return (
            <div
              className={`note-cell-text${noteReadonly ? " note-child-readonly" : ""}${!note && !noteReadonly ? " note-cell-empty" : ""}`}
              title={
                isChildNote
                  ? note || "הערת חייל (לקריאה בלבד)"
                  : isUnitPeerNoteReadonly
                    ? note || "לקריאה בלבד"
                    : note || "לחץ לעריכה"
              }
              onClick={() => {
                if (!noteReadonly) {
                  const level =
                    userRole === "childUser" ? "second" : "first";
                  let personalNumber =
                    item.assignedPersonnel?.[0]?.personalNumber;
                  if (!personalNumber) {
                    const raw = item.assignments?.[level] || {};
                    personalNumber = Object.keys(raw)[0];
                  }
                  setEditingNoteId(item.id);
                  setNoteValue(note);
                  setEditingNoteMeta({
                    itemId: item.id,
                    personalNumber,
                    level,
                    originalNote: note,
                  });
                }
              }}
            >
              {note || <span className="note-placeholder">+</span>}
            </div>
          );
            })()
          )}
        </div>
      )}
      {!hideGroupedColumns && (
        <>
      <div
        className="virtual-cell"
        style={getColumnStyle("seenAt", columnWidths)}
      >
        {(() => {
          const serial =
            !isGroup && item.serialNumber && item.serialNumber.trim() !== "";
          if (!serial) return "";

          const seenStatus = getSeenStatus(
            item.seenAt,
            seenSettings.greenMax,
            seenSettings.yellowDays
          );
          const days = seenStatus?.days;
          return (
            <div
              className={`seen-cell ${
                seenStatus?.colorClass || "seen-no-data"
              }`}
            >
              <div className="seen-days">{days != null ? days : "-"}</div>
            </div>
          );
        })()}
      </div>
      <div
        className="virtual-cell"
        style={getColumnStyle("actions", columnWidths)}
      >
        <div className="action-buttons">
          {isGroup ? null : (
            <>
          {(() => {
            return isSerial ? (
              <button
                onClick={() => handleConfirmSeen(item.id, item.seenAt)}
                className="seen-btn"
                title={
                  shouldClearSeenOnEyeClick(
                    item.seenAt,
                    seenSettings.greenMax,
                    seenSettings.yellowDays
                  )
                    ? "בטל סימון נראה (בטעות)"
                    : "אשר נראה"
                }
              >
                <FaEye />
              </button>
            ) : null;
          })()}
          {userRole !== "childUser" && (
            <button
              onClick={() => handleEdit(item)}
              className="edit-btn"
              title="ערוך"
            >
              <FaEdit />
            </button>
          )}
          {userRole !== "childUser" &&
            (item.status === "returned" ? (
              <button
                onClick={() => handleMakeAvailable(item.id)}
                className="available-btn"
                title="החזר לזמין"
              >
                <FaArrowRight />
              </button>
            ) : (
              <button
                onClick={() => handleReturnClick(item)}
                className="return-btn"
                disabled={
                  item.serialNumber &&
                  (item.status === "assigned" ||
                    item.calculatedStatus === "assigned")
                }
                title={
                  item.serialNumber &&
                  (item.status === "assigned" ||
                    item.calculatedStatus === "assigned")
                    ? "לא ניתן להחזיר פריט שמוחתם"
                    : item.serialNumber
                    ? "זיכוי"
                    : "זיכוי (בחר כמות)"
                }
              >
                <FaArrowLeft />
              </button>
            ))}
          {userPermissions === "admin" && (
            <button
              onClick={() => handleDelete(item.id)}
              className="delete-btn"
              title="מחק"
            >
              <FaTrash />
            </button>
          )}
            </>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
});

const VirtualInventoryTable = ({
  items,
  disableSort = false,
  hideGroupedColumns = false,
  serialViewMode = null,
  onToggleSerialViewMode = null,
  getUnitName,
  personnelUnitMap,
  handleEdit,
  handleReturn,
  handleMakeAvailable,
  handleDelete,
  handleConfirmSeen,
  sortField,
  sortDirection,
  handleSort,
  userPermissions,
  userRole,
  childUserUnitId,
  childUserPersonalNumber = null,
  seenSettings = { greenMax: 2, yellowDays: 3 },
  onOpenSeenSettings,
  resolveAssignedByName,
  onItemNameClick,
  onAssignedNameClick,
  onUpdateNote,
  /** Parent reads scrollTop before דוח צ click; optional */
  scrollContainerRef = null,
  /** { current: { applyScrollTop(n) } } — sync virtual list after programmatic scroll */
  virtualScrollApiRef = null,
  nicknameByPersonalNumber = new Map(),
  personnel = null,
}) => {
  const ITEM_HEIGHT = 60;

  // Responsive container height based on viewport
  const [containerHeight, setContainerHeight] = React.useState(() => {
    if (typeof window !== "undefined") {
      // Reserve space for header, stats, controls (approximately 300px)
      const availableHeight = window.innerHeight - 300;
      // Use at least 400px, but up to 90% of available height, max 1000px
      return Math.max(400, Math.min(availableHeight * 0.9, 1000));
    }
    return 500;
  });

  // Update container height on window resize
  React.useEffect(() => {
    const handleResize = () => {
      const availableHeight = window.innerHeight - 300;
      const newHeight = Math.max(400, Math.min(availableHeight * 0.9, 1000));
      setContainerHeight(newHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    visibleItems,
    totalHeight,
    offsetY,
    setContainerRef,
    handleScroll,
    applyScrollTop,
  } = useVirtualScroll(items, ITEM_HEIGHT, containerHeight);

  const setScrollContainerEl = useCallback(
    (node) => {
      setContainerRef(node);
      if (scrollContainerRef) {
        scrollContainerRef.current = node;
      }
    },
    [setContainerRef, scrollContainerRef],
  );

  React.useEffect(() => {
    if (!virtualScrollApiRef) return undefined;
    virtualScrollApiRef.current = { applyScrollTop };
    return () => {
      virtualScrollApiRef.current = null;
    };
  }, [virtualScrollApiRef, applyScrollTop]);

  // Optimized state management
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);

  const displayColumnWidths = useMemo(() => {
    if (!hideGroupedColumns) return columnWidths;
    const out = { ...columnWidths };
    for (const key of Object.keys(out)) {
      out[key] = Math.min(
        GROUPED_MODE_MAX_COLUMN_WIDTH,
        (out[key] ?? DEFAULT_COLUMN_WIDTHS[key] ?? 80) +
          GROUPED_MODE_COLUMN_WIDTH_EXTRA,
      );
    }
    return out;
  }, [columnWidths, hideGroupedColumns]);

  // Return quantity modal state
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState(1);

  // Inline note editing state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [editingNoteMeta, setEditingNoteMeta] = useState(null); // { itemId, personalNumber, level, originalNote }

  // Optimized column resize handler
  const handleMouseDown = useCallback(
    (e, columnKey) => {
      e.preventDefault();
      setIsResizing(true);
      setResizingColumn(columnKey);

      const startX = e.clientX;
      const startWidth = columnWidths[columnKey];

      const handleMouseMove = (e) => {
        const newWidth = Math.max(50, startWidth + (e.clientX - startX));
        setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        setResizingColumn(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths]
  );

  // Handle return with quantity for non-serial items
  const handleReturnClick = useCallback(
    (item) => {
      if (item.isSerialGroupRow) return;
      // Check if serial item is assigned - can't return assigned items
      if (item.serialNumber) {
        // Serial item - check if assigned
        if (
          item.status === "assigned" ||
          item.calculatedStatus === "assigned"
        ) {
          alert(
            "לא ניתן להחזיר פריט שמוחתם. יש להחזיר אותו דרך מסך החתימה/זיכוי תחילה."
          );
          return;
        }
        // Serial item - return immediately (only if available)
        handleReturn(item.id);
      } else {
        // Non-serial item - check available quantity
        const availableQty = item.availableQuantity ?? item.quantity ?? 0;
        if (availableQty <= 0) {
          alert("אין כמות זמינה להחזרה. הפריט מוחתם במלואו.");
          return;
        }
        // Non-serial item - show quantity modal with available quantity limit
        setSelectedItem(item);
        setReturnQuantity(1);
        setShowReturnModal(true);
      }
    },
    [handleReturn]
  );

  // Handle return quantity confirmation
  const handleReturnConfirm = useCallback(() => {
    if (!selectedItem) return;

    // For non-serial items, use availableQuantity instead of quantity
    const maxQuantity =
      selectedItem.availableQuantity ?? selectedItem.quantity ?? 0;

    if (returnQuantity > 0 && returnQuantity <= maxQuantity) {
      handleReturn(selectedItem.id, returnQuantity);
      setShowReturnModal(false);
      setSelectedItem(null);
      setReturnQuantity(1);
    } else {
      alert(`כמות לא תקינה. ניתן להחזיר עד ${maxQuantity} יחידות.`);
    }
  }, [selectedItem, returnQuantity, handleReturn]);

  // Close return modal
  const handleReturnCancel = useCallback(() => {
    setShowReturnModal(false);
    setSelectedItem(null);
    setReturnQuantity(1);
  }, []);

  // Memoized table headers with configuration-driven approach
  const TableHeader = useMemo(() => {
    const allowSort = !disableSort;
    const hg = hideGroupedColumns;
    return (
      <div className="virtual-header-row">
        <div
          className={
            allowSort
              ? "virtual-header-cell sortable"
              : "virtual-header-cell"
          }
          onClick={allowSort ? () => handleSort("itemName") : undefined}
          title="לחץ למיון"
          style={getColumnStyle("itemName", displayColumnWidths)}
        >
          שם הפריט
          {sortField === "itemName" && (
            <span className="sort-indicator">
              {sortDirection === "asc" ? "↑" : "↓"}
            </span>
          )}
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, "itemName")}
            title="גרור לשינוי גודל"
          />
        </div>
        <div
          className={
            allowSort
              ? "virtual-header-cell sortable"
              : "virtual-header-cell"
          }
          onClick={allowSort ? () => handleSort("serialNumber") : undefined}
          title="לחץ למיון"
          style={getColumnStyle("serialNumber", displayColumnWidths)}
        >
          {hg ? "סטטוס" : "מספר סידורי"}
          {sortField === "serialNumber" && (
            <span className="sort-indicator">
              {sortDirection === "asc" ? "↑" : "↓"}
            </span>
          )}
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, "serialNumber")}
            title="גרור לשינוי גודל"
          />
        </div>
        <div
          className={
            allowSort
              ? "virtual-header-cell sortable"
              : "virtual-header-cell"
          }
          onClick={allowSort ? () => handleSort("isOrganic") : undefined}
          title="לחץ למיון"
          style={getColumnStyle("isOrganic", displayColumnWidths)}
        >
          סוג
          {sortField === "isOrganic" && (
            <span className="sort-indicator">
              {sortDirection === "asc" ? "↑" : "↓"}
            </span>
          )}
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, "isOrganic")}
            title="גרור לשינוי גודל"
          />
        </div>
        <div
          className={
            allowSort
              ? "virtual-header-cell sortable"
              : "virtual-header-cell"
          }
          onClick={allowSort ? () => handleSort("quantity") : undefined}
          title="לחץ למיון"
          style={getColumnStyle("quantity", displayColumnWidths)}
        >
          כמות כללית
          {sortField === "quantity" && (
            <span className="sort-indicator">
              {sortDirection === "asc" ? "↑" : "↓"}
            </span>
          )}
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, "quantity")}
            title="גרור לשינוי גודל"
          />
        </div>
        <div
          className="virtual-header-cell"
          style={getColumnStyle("assignedQuantity", displayColumnWidths)}
        >
          כמות מוקצית
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, "assignedQuantity")}
            title="גרור לשינוי גודל"
          />
        </div>
        <div
          className="virtual-header-cell"
          style={getColumnStyle("availableQuantity", displayColumnWidths)}
        >
          כמות זמינה
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, "availableQuantity")}
            title="גרור לשינוי גודל"
          />
        </div>
        {!hg && (
          <>
            <div
              className={
                allowSort
                  ? "virtual-header-cell sortable"
                  : "virtual-header-cell"
              }
              onClick={allowSort ? () => handleSort("status") : undefined}
              title="לחץ למיון"
              style={getColumnStyle("status", displayColumnWidths)}
            >
              סטטוס
              {sortField === "status" && (
                <span className="sort-indicator">
                  {sortDirection === "asc" ? "↑" : "↓"}
                </span>
              )}
              <div
                className="resize-handle"
                onMouseDown={(e) => handleMouseDown(e, "status")}
                title="גרור לשינוי גודל"
              />
            </div>
            <div
              className={
                allowSort
                  ? "virtual-header-cell sortable"
                  : "virtual-header-cell"
              }
              onClick={allowSort ? () => handleSort("assignedTo") : undefined}
              title="לחץ: א-ת · זמינים קודם · ת-א"
              style={getColumnStyle("assignedTo", displayColumnWidths)}
            >
              חתום ל
              {sortField === "assignedTo" && (
                <span className="sort-indicator">
                  {sortDirection === "asc"
                    ? "↑"
                    : sortDirection === "avail"
                      ? "·"
                      : "↓"}
                </span>
              )}
              <div
                className="resize-handle"
                onMouseDown={(e) => handleMouseDown(e, "assignedTo")}
                title="גרור לשינוי גודל"
              />
            </div>
            {userRole !== "childUser" && (
              <div
                className="virtual-header-cell"
                style={getColumnStyle("assignedBy", displayColumnWidths)}
              >
                הוקצה על ידי
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleMouseDown(e, "assignedBy")}
                  title="גרור לשינוי גודל"
                />
              </div>
            )}
            <div
              className={
                allowSort
                  ? "virtual-header-cell sortable"
                  : "virtual-header-cell"
              }
              onClick={
                allowSort ? () => handleSort("currentUnitId") : undefined
              }
              title="לחץ למיון"
              style={getColumnStyle("currentUnitId", displayColumnWidths)}
            >
              יחידה
              {sortField === "currentUnitId" && (
                <span className="sort-indicator">
                  {sortDirection === "asc" ? "↑" : "↓"}
                </span>
              )}
              <div
                className="resize-handle"
                onMouseDown={(e) => handleMouseDown(e, "currentUnitId")}
                title="גרור לשינוי גודל"
              />
            </div>
          </>
        )}
        {!hg && (
          <div
            className="virtual-header-cell"
            style={getColumnStyle("locationNote", displayColumnWidths)}
          >
            מיקום/הערה
            <div
              className="resize-handle"
              onMouseDown={(e) => handleMouseDown(e, "locationNote")}
              title="גרור לשינוי גודל"
            />
          </div>
        )}
        {!hg && (
          <>
            <div
              className={
                allowSort
                  ? "virtual-header-cell sortable"
                  : "virtual-header-cell"
              }
              onClick={allowSort ? () => handleSort("seenAt") : undefined}
              title="לחץ למיון לפי ימים מאז דוח צ (חסר = בסוף במעלה)"
              style={getColumnStyle("seenAt", displayColumnWidths)}
            >
              דוח צ
              {sortField === "seenAt" && (
                <span className="sort-indicator">
                  {sortDirection === "asc" ? "↑" : "↓"}
                </span>
              )}
              <div
                className="resize-handle"
                onMouseDown={(e) => handleMouseDown(e, "seenAt")}
                title="גרור לשינוי גודל"
              />
            </div>
            <div
              className="virtual-header-cell"
              style={getColumnStyle("actions", displayColumnWidths)}
            >
              פעולות
              <div
                className="resize-handle"
                onMouseDown={(e) => handleMouseDown(e, "actions")}
                title="גרור לשינוי גודל"
              />
            </div>
          </>
        )}
      </div>
    );
  }, [
    sortField,
    sortDirection,
    handleSort,
    displayColumnWidths,
    handleMouseDown,
    disableSort,
    hideGroupedColumns,
    userRole,
  ]);

  return (
    <>
      <div
        className={`virtual-table-container${
          hideGroupedColumns ? " virtual-table-grouped-columns" : ""
        }`}
      >
        {onToggleSerialViewMode && serialViewMode ? (
          <div
            className="virtual-table-items-count inventory-table-items-count-with-toggle"
            role="status"
          >
            <span className="inventory-items-count-text">
              מוצגים {items.length} פריטים
            </span>
            <button
              type="button"
              className="inventory-serial-view-toggle"
              onClick={onToggleSerialViewMode}
              aria-pressed={serialViewMode === "grouped"}
              title={
                serialViewMode === "grouped"
                  ? "הצג כל פריט סידורי בשורה נפרדת"
                  : "קבץ פריטי סידורי לפי סוג (כמו פריט ללא סידורי)"
              }
            >
              {serialViewMode === "grouped" ? (
                <FaList aria-hidden />
              ) : (
                <FaLayerGroup aria-hidden />
              )}
            </button>
          </div>
        ) : (
          <div className="virtual-table-items-count" role="status">
            מוצגים {items.length} פריטים
          </div>
        )}
        {TableHeader}
        <div
          ref={setScrollContainerEl}
          className="virtual-scroll-container"
          style={{
            height: containerHeight,
            overflowY: "auto",
            position: "relative",
          }}
          onScroll={handleScroll}
        >
          <div
            style={{
              height: totalHeight,
              position: "relative",
            }}
          >
            <div
              style={{
                transform: `translateY(${offsetY}px)`,
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
              }}
            >
              {visibleItems.map((item) => (
                <VirtualInventoryRow
                  key={item.id}
                  item={item}
                  height={ITEM_HEIGHT}
                  columnWidths={displayColumnWidths}
                  hideGroupedColumns={hideGroupedColumns}
                  getUnitName={getUnitName}
                  personnelUnitMap={personnelUnitMap}
                  handleEdit={handleEdit}
                  handleReturnClick={handleReturnClick}
                  handleMakeAvailable={handleMakeAvailable}
                  handleDelete={handleDelete}
                  handleConfirmSeen={handleConfirmSeen}
                  userPermissions={userPermissions}
                  userRole={userRole}
                  childUserUnitId={childUserUnitId}
                  childUserPersonalNumber={childUserPersonalNumber}
                  resolveAssignedByName={resolveAssignedByName}
                  onItemNameClick={onItemNameClick}
                  onAssignedNameClick={onAssignedNameClick}
                  seenSettings={seenSettings}
                  editingNoteId={editingNoteId}
                  setEditingNoteId={setEditingNoteId}
                  noteValue={noteValue}
                  setNoteValue={setNoteValue}
                  editingNoteMeta={editingNoteMeta}
                  setEditingNoteMeta={setEditingNoteMeta}
                  onUpdateNote={onUpdateNote}
                  nicknameByPersonalNumber={nicknameByPersonalNumber}
                  personnel={personnel}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Return Quantity Modal */}
      {showReturnModal && selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>זיכוי פריט</h3>
              <button
                className="modal-close"
                onClick={handleReturnCancel}
                title="סגור"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>פריט:</strong> {selectedItem.itemName}
              </p>
              <p>
                <strong>כמות זמינה:</strong>{" "}
                {selectedItem.availableQuantity ?? selectedItem.quantity ?? 0}
              </p>
              <div className="form-group">
                <label htmlFor="returnQuantity">כמות לזיכוי:</label>
                <input
                  id="returnQuantity"
                  type="number"
                  min="1"
                  max={
                    selectedItem.availableQuantity ?? selectedItem.quantity ?? 0
                  }
                  value={returnQuantity}
                  onChange={(e) =>
                    setReturnQuantity(parseInt(e.target.value) || 1)
                  }
                  onFocus={(e) => e.target.select()}
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleReturnCancel}
              >
                ביטול
              </button>
              <button
                className="btn btn-primary"
                onClick={handleReturnConfirm}
                disabled={
                  returnQuantity <= 0 ||
                  returnQuantity >
                    (selectedItem.availableQuantity ??
                      selectedItem.quantity ??
                      0)
                }
              >
                אישור זיכוי
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VirtualInventoryTable;
